import { createFileRoute } from "@tanstack/react-router";
import { createServerFn, useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { NURTURE_SEQUENCE, NURTURE_SENDER, NURTURE_COUPON_CODE } from "@/lib/nurture-templates";
import { useState } from "react";
import { usePermissions } from "@/hooks/use-permissions";
import { BackButton } from "@/components/BackButton";
import {
  PermissionLock,
  PermissionLockLoading,
} from "@/components/admin/PermissionLock";

const getNurtureStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: roleRows } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .in("role", ["super_admin", "admin", "content_manager"]);
    if (!roleRows || roleRows.length === 0) throw new Error("forbidden");

    const { data: rows, error } = await supabaseAdmin
      .from("nurture_subscribers")
      .select("current_step, opted_out, converted, opens_count, clicks_count, enrolled_at, next_send_at, email, locale")
      .order("enrolled_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);

    const total = rows.length;
    const active = rows.filter((r) => !r.opted_out && !r.converted).length;
    const converted = rows.filter((r) => r.converted).length;
    const optedOut = rows.filter((r) => r.opted_out).length;
    const byStep = [0, 1, 2, 3, 4].map((s) => ({
      step: s,
      count: rows.filter((r) => r.current_step === s && !r.opted_out && !r.converted).length,
    }));
    const totalOpens = rows.reduce((s, r) => s + (r.opens_count ?? 0), 0);
    const totalClicks = rows.reduce((s, r) => s + (r.clicks_count ?? 0), 0);
    const sent = rows.filter((r) => r.current_step > 0).length;

    return {
      total,
      active,
      converted,
      optedOut,
      byStep,
      openRate: sent ? totalOpens / sent : 0,
      clickRate: sent ? totalClicks / sent : 0,
      conversionRate: total ? converted / total : 0,
      recent: rows.slice(0, 50),
    };
  });

const setOptOut = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { email: string; opted_out: boolean }) =>
    z.object({ email: z.string().email().max(320), opted_out: z.boolean() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { data: roleRows } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .in("role", ["super_admin", "admin", "content_manager"]);
    if (!roleRows || roleRows.length === 0) throw new Error("forbidden");
    const { error } = await supabaseAdmin
      .from("nurture_subscribers")
      .update({
        opted_out: data.opted_out,
        opted_out_at: data.opted_out ? new Date().toISOString() : null,
        next_send_at: data.opted_out ? null : new Date(Date.now() + 86_400_000).toISOString(),
      })
      .eq("email", data.email.toLowerCase());
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const Route = createFileRoute("/_authenticated/app/admin/nurture")({
  component: AdminNurture,
  head: () => ({ meta: [{ title: "Nurture Campaign — Admin" }] }),
});

function AdminNurture() {
  const perms = usePermissions();
  const fn = useServerFn(getNurtureStats);
  const optOut = useServerFn(setOptOut);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-nurture"],
    queryFn: () => fn({ data: {} as never }),
    enabled: !perms.loading && perms.canManageNurture,
  });
  const [tab, setTab] = useState<"overview" | "templates">("overview");

  if (perms.loading) return <PermissionLockLoading />;
  if (!perms.canManageNurture) {
    return (
      <PermissionLock
        titleEn="Nurture access required"
        titleAr="صلاحية الحملات مطلوبة"
        messageEn="The nurture campaign is restricted to content managers and super admins."
        messageAr="حملات البريد متاحة لإدارة المحتوى والمسؤول العام فقط."
      />
    );
  }


  return (
    <main className="container mx-auto py-8 space-y-6">
      <div><BackButton /></div>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Lead Nurture Campaign</h1>
          <p className="text-sm text-muted-foreground">
            Auto-enrolled free users · 4 emails over ~24 days · stops on subscribe
          </p>
        </div>
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => setTab("overview")}
            className={`px-3 py-1.5 rounded-md border ${tab === "overview" ? "bg-primary text-primary-foreground" : ""}`}
          >
            Overview
          </button>
          <button
            onClick={() => setTab("templates")}
            className={`px-3 py-1.5 rounded-md border ${tab === "templates" ? "bg-primary text-primary-foreground" : ""}`}
          >
            Templates
          </button>
        </div>
      </header>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}

      {tab === "overview" && data && (
        <>
          <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Stat label="Total enrolled" value={data.total} />
            <Stat label="Active" value={data.active} />
            <Stat label="Converted" value={data.converted} hint={pct(data.conversionRate)} />
            <Stat label="Opted out" value={data.optedOut} />
            <Stat label="Open rate" value={pct(data.openRate)} hint={`Click ${pct(data.clickRate)}`} />
          </section>

          <section className="rounded-lg border p-4">
            <h2 className="font-semibold mb-3">Active enrollees by step</h2>
            <div className="grid grid-cols-5 gap-2">
              {data.byStep.map((s) => (
                <div key={s.step} className="rounded-md bg-muted/40 p-3 text-center">
                  <div className="text-xs text-muted-foreground">
                    {s.step === 0 ? "Awaiting #1" : `After email ${s.step}`}
                  </div>
                  <div className="text-2xl font-semibold">{s.count}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="p-2">Email</th>
                  <th className="p-2">Locale</th>
                  <th className="p-2">Step</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Enrolled</th>
                  <th className="p-2">Next send</th>
                  <th className="p-2">Opens / Clicks</th>
                  <th className="p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r.email} className="border-t">
                    <td className="p-2 font-mono text-xs">{r.email}</td>
                    <td className="p-2 uppercase">{r.locale}</td>
                    <td className="p-2">{r.current_step}/4</td>
                    <td className="p-2">
                      {r.converted ? (
                        <span className="text-emerald-600">Converted</span>
                      ) : r.opted_out ? (
                        <span className="text-muted-foreground">Opted out</span>
                      ) : (
                        <span className="text-amber-600">Active</span>
                      )}
                    </td>
                    <td className="p-2">{new Date(r.enrolled_at).toLocaleDateString()}</td>
                    <td className="p-2">{r.next_send_at ? new Date(r.next_send_at).toLocaleDateString() : "—"}</td>
                    <td className="p-2">
                      {r.opens_count ?? 0} / {r.clicks_count ?? 0}
                    </td>
                    <td className="p-2">
                      {!r.converted && (
                        <button
                          className="text-xs underline text-muted-foreground hover:text-foreground"
                          onClick={async () => {
                            await optOut({ data: { email: r.email, opted_out: !r.opted_out } });
                            refetch();
                          }}
                        >
                          {r.opted_out ? "Re-enroll" : "Opt out"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}

      {tab === "templates" && (
        <section className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-4 text-sm space-y-1">
            <div>
              <strong>Sender:</strong> {NURTURE_SENDER.name} &lt;{NURTURE_SENDER.email}&gt;
            </div>
            <div>
              <strong>Cadence:</strong> Day 3 → Day 10 → Day 17 → Day 24
            </div>
            <div>
              <strong>Coupon (email 4):</strong> <code>{NURTURE_COUPON_CODE}</code> · 10% off · 3-day per-user
              validity
            </div>
            <div className="text-muted-foreground">
              Tokens: <code>{"{{NAME}}"}</code> <code>{"{{COUPON}}"}</code> <code>{"{{COUPON_EXPIRES}}"}</code>{" "}
              <code>{"{{APP_URL}}"}</code> <code>{"{{UNSUB_URL}}"}</code>
            </div>
          </div>
          {(["en", "ar"] as const).map((locale) => (
            <div key={locale} className="space-y-3">
              <h2 className="text-lg font-semibold">{locale === "en" ? "English" : "العربية"}</h2>
              {NURTURE_SEQUENCE[locale].map((t) => (
                <details key={t.step} className="rounded-md border p-3" dir={locale === "ar" ? "rtl" : "ltr"}>
                  <summary className="cursor-pointer font-medium">
                    Step {t.step} · Day {t.delayDays} · {t.subject}
                  </summary>
                  <div className="mt-3 space-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Preheader:</span> {t.preheader}
                    </div>
                    <pre className="whitespace-pre-wrap bg-muted/30 rounded p-3 text-xs">{t.body}</pre>
                    <div className="text-xs text-muted-foreground">
                      CTA: <strong>{t.cta.label}</strong> → <code>{t.cta.url}</code>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          ))}
        </section>
      )}
    </main>
  );
}

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="rounded-lg border p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function pct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}
